import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Eye, Pencil, Trash2, FileSearch, ShoppingCart, AlertTriangle, Check, Loader2, FileUp, Building2, ArrowRight, Archive, Receipt, MoreHorizontal } from "lucide-react";
import { supplierAPI, supplierQuoteAPI, supplierInvoiceAPI } from "@/lib/api";
import { CompanyLookup } from "@/components/CompanyLookup";
import { toast } from "sonner";
import { BulkActionBar, useSelection } from "@/components/BulkActionBar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function joinTruthy(arr, sep = " - ") { return arr.filter(Boolean).join(sep); }

const emptyForm = { company: "", contact_name: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "France", siret: "", notes: "" };

function DevisUploadFlow({ open, onOpenChange, onComplete }) {
  const [step, setStep] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ company: "", email: "", phone: "", address: "", city: "", postal_code: "", siret: "" });
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [dragover, setDragover] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setStep("upload"); setAnalysis(null); setSelectedSupplierId(""); setSupplierForm({ company: "", email: "", phone: "", address: "", city: "", postal_code: "", siret: "" }); }
  }, [open]);

  const handleFile = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith(".pdf")) { toast.error("Fichier PDF requis"); return; }
    setUploading(true);
    try {
      const res = await supplierQuoteAPI.analyze(file);
      setAnalysis(res.data);
      const si = res.data.supplier_info || {};
      setSupplierForm({ company: si.name || "", email: si.email || "", phone: si.phone || "", address: si.address || "", city: si.city || "", postal_code: si.postal_code || "", siret: si.siret || "" });
      if (res.data.supplier_match?.exact_matches?.length) {
        setSelectedSupplierId(res.data.supplier_match.exact_matches[0].id);
      }
      setStep("validate");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur analyse PDF"); }
    finally { setUploading(false); }
  };

  const handleConfirm = async () => {
    if (!selectedSupplierId && !supplierForm.company.trim()) { toast.error("Nom fournisseur requis"); return; }
    setConfirming(true);
    try {
      const payload = {
        extracted_data: analysis.extracted_data,
        original_filename: analysis.original_filename,
      };
      if (selectedSupplierId) {
        payload.supplier_id = selectedSupplierId;
      } else {
        payload.new_supplier = { ...supplierForm, name: supplierForm.company };
      }
      const res = await supplierQuoteAPI.confirm(payload);
      toast.success("Devis fournisseur enregistré");
      onComplete(res.data.supplier, res.data.quote);
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur confirmation"); }
    finally { setConfirming(false); }
  };

  const exactMatches = analysis?.supplier_match?.exact_matches || [];
  const closeMatches = analysis?.supplier_match?.close_matches || [];
  const items = analysis?.extracted_data?.items || [];
  const isNewSupplier = !selectedSupplierId;

  const sf = (k, v) => { setSupplierForm({ ...supplierForm, [k]: v }); setSelectedSupplierId(""); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{step === "upload" ? "Nouveau devis fournisseur" : "Valider le devis"}</DialogTitle>
          <DialogDescription>{step === "upload" ? "Deposez le PDF du devis a analyser" : `${analysis?.original_filename} - ${items.length} article(s) détecté(s)`}</DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragover ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-400"}`}
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={e => { e.preventDefault(); setDragover(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            data-testid="devis-upload-zone"
          >
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={40} className="text-indigo-600 animate-spin" />
                <p className="text-indigo-600 font-medium">Analyse du PDF en cours...</p>
                <p className="text-sm text-slate-400">Extraction des articles et du fournisseur</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <FileUp size={40} className="text-slate-400" strokeWidth={1.5} />
                <p className="text-slate-600 font-medium">Deposez un devis fournisseur PDF ici</p>
                <p className="text-sm text-slate-400">ou cliquez pour parcourir</p>
              </div>
            )}
          </div>
        )}

        {step === "validate" && (
          <div className="space-y-5">
            {/* Supplier section */}
            <div className="rounded-lg border border-slate-200 p-4" data-testid="supplier-validation-section">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={16} className="text-slate-500" />
                <h4 className="font-semibold text-sm text-slate-900">Fournisseur</h4>
              </div>

              {exactMatches.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Fournisseur existant détecté</p>
                  {exactMatches.map(s => (
                    <button key={s.id} onClick={() => setSelectedSupplierId(s.id)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${selectedSupplierId === s.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}
                      data-testid={`match-supplier-${s.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{s.company}</p>
                          <p className="text-xs text-slate-500">{joinTruthy([s.email, s.phone, s.city, s.siret])}</p>
                        </div>
                        {selectedSupplierId === s.id && <Check size={16} className="text-emerald-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {closeMatches.length > 0 && !exactMatches.length && (
                <div className="mb-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-amber-600">
                    <AlertTriangle size={13} />
                    <p className="text-xs font-medium uppercase tracking-wider">Fournisseurs similaires</p>
                  </div>
                  {closeMatches.map(s => (
                    <button key={s.id} onClick={() => setSelectedSupplierId(s.id)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${selectedSupplierId === s.id ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-amber-300"}`}
                      data-testid={`close-supplier-${s.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{s.company}</p>
                          <p className="text-xs text-slate-500">{joinTruthy([s.email, s.city])}</p>
                        </div>
                        {selectedSupplierId === s.id && <Check size={16} className="text-amber-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className={`${(exactMatches.length > 0 || closeMatches.length > 0) ? "mt-3 pt-3 border-t border-slate-100" : ""}`}>
                <button onClick={() => setSelectedSupplierId("")}
                  className={`w-full text-left mb-2 text-xs font-medium uppercase tracking-wider ${isNewSupplier ? "text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}
                  data-testid="use-new-supplier-btn"
                >
                  {exactMatches.length > 0 || closeMatches.length > 0 ? "Creer un nouveau fournisseur a la place" : "Nouveau fournisseur (extrait du PDF)"}
                </button>
                {isNewSupplier && (
                  <div className="space-y-3 bg-slate-50 rounded-lg p-3" data-testid="new-supplier-form">
                    <div><Label className="text-xs">Société *</Label><Input value={supplierForm.company} onChange={e => sf("company", e.target.value)} className="h-8 text-sm" data-testid="new-sup-company" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div><Label className="text-xs">Email</Label><Input type="email" value={supplierForm.email} onChange={e => sf("email", e.target.value)} className="h-8 text-sm" data-testid="new-sup-email" /></div>
                      <div><Label className="text-xs">Téléphone</Label><Input value={supplierForm.phone} onChange={e => sf("phone", e.target.value)} className="h-8 text-sm" data-testid="new-sup-phone" /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div><Label className="text-xs">Adresse</Label><Input value={supplierForm.address} onChange={e => sf("address", e.target.value)} className="h-8 text-sm" /></div>
                      <div><Label className="text-xs">Ville</Label><Input value={supplierForm.city} onChange={e => sf("city", e.target.value)} className="h-8 text-sm" /></div>
                      <div><Label className="text-xs">SIRET</Label><Input value={supplierForm.siret} onChange={e => sf("siret", e.target.value)} className="h-8 text-sm font-mono" data-testid="new-sup-siret" /></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Items section */}
            <div className="rounded-lg border border-slate-200 p-4" data-testid="items-validation-section">
              <h4 className="font-semibold text-sm text-slate-900 mb-3">Articles extraits ({items.length})</h4>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] uppercase text-slate-500">Produit</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500 text-center">Qté</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500 text-right">Prix HT (€)</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500 text-right">TVA</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={item.name || item.product_name || `sup-item-${idx}`}>
                        <TableCell className="text-sm font-medium max-w-[200px] truncate">{item.name || item.product_name || "-"}</TableCell>
                        <TableCell className="text-sm text-center">{item.quantity || 1}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{parseFloat(item.unit_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-sm text-right">{item.tva_rate || 20}%</TableCell>
                      </TableRow>
                    ))}
                    {items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-4">Aucun article détecté</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>

            <Button onClick={handleConfirm} disabled={confirming || (isNewSupplier && !supplierForm.company.trim())}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider"
              data-testid="confirm-devis-btn"
            >
              {confirming ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ArrowRight size={14} className="mr-2" />}
              {isNewSupplier ? "Créer le fournisseur et enregistrer le devis" : "Associer et enregistrer le devis"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SupplierPickerDialog({ open, onOpenChange, actionType, onSupplierSelected }) {
  const [step, setStep] = useState("search");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [exactMatches, setExactMatches] = useState([]);
  const [closeMatches, setCloseMatches] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ company: "", email: "", phone: "", address: "" });

  useEffect(() => {
    if (!open) { setStep("search"); setQuery(""); setResults([]); setExactMatches([]); setCloseMatches([]); setForm({ company: "", email: "", phone: "", address: "" }); }
  }, [open]);

  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) { setResults([]); setExactMatches([]); setCloseMatches([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const [listRes, matchRes] = await Promise.all([
          supplierAPI.list({ search: query.trim(), limit: 10 }),
          supplierAPI.match({ company: query.trim() }),
        ]);
        setResults((listRes.data.items || listRes.data || []).slice(0, 8));
        setExactMatches(matchRes.data.exact_matches || []);
        setCloseMatches(matchRes.data.close_matches || []);
      } catch (e) { toast.error("Erreur recherche fournisseur"); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const selectSupplier = (supplier) => {
    onSupplierSelected(supplier, actionType);
    onOpenChange(false);
  };

  const startCreate = () => {
    setForm({ company: query.trim(), email: "", phone: "", address: "" });
    setStep("create");
  };

  const handleCreate = async () => {
    if (!form.company.trim()) { toast.error("Le nom est obligatoire"); return; }
    setCreating(true);
    try {
      const res = await supplierAPI.create(form);
      toast.success("Fournisseur créé");
      onSupplierSelected(res.data, actionType);
      onOpenChange(false);
    } catch { toast.error("Erreur creation"); }
    finally { setCreating(false); }
  };

  const actionLabel = actionType === "devis" ? "un devis fournisseur" : "une commande fournisseur";
  const hasExact = exactMatches.length > 0;
  const hasClose = closeMatches.length > 0 && !hasExact;
  const hasResults = results.length > 0;
  const noResults = query.trim().length >= 2 && !searching && !hasResults;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Choisir ou creer un fournisseur</DialogTitle>
          <DialogDescription>Pour {actionLabel}</DialogDescription>
        </DialogHeader>

        {step === "search" && (
          <div className="space-y-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email, SIRET..."
                value={query} onChange={e => setQuery(e.target.value)}
                className="pl-9" autoFocus
                data-testid="supplier-picker-search"
              />
            </div>

            {searching && (
              <div className="flex items-center justify-center py-4 text-slate-400">
                <Loader2 size={16} className="animate-spin mr-2" /> Recherche...
              </div>
            )}

            {hasExact && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Correspondance exacte</p>
                {exactMatches.map(s => (
                  <button key={s.id} onClick={() => selectSupplier(s)} className="w-full text-left p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors" data-testid={`exact-match-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{s.company}</p>
                        <p className="text-xs text-slate-500">{joinTruthy([s.email, s.phone, s.city])}</p>
                      </div>
                      <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px]">Existant</Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {hasClose && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-amber-600">
                  <AlertTriangle size={13} />
                  <p className="text-xs font-medium uppercase tracking-wider">Fournisseurs similaires trouves</p>
                </div>
                {closeMatches.map(s => (
                  <button key={s.id} onClick={() => selectSupplier(s)} className="w-full text-left p-3 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors" data-testid={`close-match-${s.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{s.company}</p>
                        <p className="text-xs text-slate-500">{joinTruthy([s.email, s.city])}</p>
                      </div>
                      <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Similaire</Badge>
                    </div>
                  </button>
                ))}
                <p className="text-xs text-slate-500">Sélectionnez un fournisseur existant ou creez-en un nouveau ci-dessous.</p>
              </div>
            )}

            {!hasExact && hasResults && !hasClose && (
              <div className="space-y-1">
                <p className="text-xs text-slate-500 mb-1">{results.length} résultat(s)</p>
                {results.map(s => (
                  <button key={s.id} onClick={() => selectSupplier(s)} className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors" data-testid={`result-${s.id}`}>
                    <p className="text-sm font-medium">{s.company}</p>
                    <p className="text-xs text-slate-400">{joinTruthy([s.email, s.phone, s.city])}</p>
                  </button>
                ))}
              </div>
            )}

            {noResults && (
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-3">Aucun fournisseur trouve pour "{query}"</p>
              </div>
            )}

            {query.trim().length >= 2 && !hasExact && (
              <Button onClick={startCreate} variant="outline" className="w-full border-dashed border-slate-300 text-slate-600 hover:text-slate-900" data-testid="create-new-supplier-btn">
                <Plus size={14} className="mr-2" /> Creer le fournisseur "{query.trim()}"
              </Button>
            )}
          </div>
        )}

        {step === "create" && (
          <div className="space-y-4">
            <div>
              <Label>Nom de la société *</Label>
              <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} autoFocus data-testid="quick-supplier-company" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="quick-supplier-email" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="quick-supplier-phone" /></div>
            </div>
            <div><Label>Adresse</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="quick-supplier-address" /></div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("search")} className="flex-1">Retour</Button>
              <Button onClick={handleCreate} disabled={creating || !form.company.trim()} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid="confirm-quick-create-btn">
                {creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />}
                Creer et continuer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [devisFlowOpen, setDevisFlowOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlNew = searchParams.get("new");

  const load = useCallback(() => {
    const params = search ? { search } : {};
    supplierAPI.list(params).then(r => setSuppliers(r.data.items || r.data)).catch(() => {});
  }, [search]);
  useEffect(() => { load(); }, [load]);

  // Auto-repair orphan supplier links on first load
  useEffect(() => {
    supplierInvoiceAPI.repairSuppliers().catch(() => {});
  }, []);

  useEffect(() => {
    if (urlNew === "devis") setDevisFlowOpen(true);
    else if (urlNew === "commande") setPickerOpen(true);
    else if (urlNew === "supplier") { setEditing(null); setForm({ ...emptyForm }); setShowDialog(true); }
  }, [urlNew]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setShowDialog(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setShowDialog(true); };

  const save = async () => {
    if (!form.company.trim()) { toast.error("La société est requise"); return; }
    try {
      if (editing) { await supplierAPI.update(editing.id, form); toast.success("Fournisseur modifié"); }
      else { await supplierAPI.create(form); toast.success("Fournisseur créé"); }
      setShowDialog(false); load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce fournisseur ?")) return;
    await supplierAPI.delete(id); toast.success("Supprimé"); load();
  };

  const openPicker = () => { setPickerOpen(true); };

  const handleSupplierSelected = (supplier) => {
    navigate(`/fournisseurs/${supplier.id}?tab=orders`);
  };

  const handleDevisComplete = (supplier, quote) => {
    navigate(`/fournisseurs/${supplier.id}?tab=pdf`);
  };

  const f = (k, v) => setForm({ ...form, [k]: v });

  const selection = useSelection(suppliers);
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleBulkAction = async (actionKey) => {
    if (!selection.selectedIds.length) return;
    if (actionKey === "delete" && !window.confirm(`Supprimer ${selection.count} fournisseur(s) ?`)) return;
    setBulkLoading(true);
    try {
      const res = await supplierAPI.bulkAction(selection.selectedIds, actionKey);
      toast.success(res.data.message);
      selection.clear();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur action groupée"); }
    finally { setBulkLoading(false); }
  };

  const supplierBulkActions = [
    { key: "delete", label: "Supprimer", icon: Trash2, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50" },
    { key: "archive", label: "Archiver", icon: Archive, variant: "outline", className: "text-orange-600 border-orange-200 hover:bg-orange-50" },
  ];

  return (
    <div className="page-container" data-testid="suppliers-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Fournisseurs</h1>
        <p className="text-slate-500 mt-1">Gerez vos fournisseurs et commandes</p>
      </div>
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="supplier-search" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setDevisFlowOpen(true)} className="text-xs font-semibold uppercase tracking-wider" data-testid="new-supplier-quote-btn">
              <FileSearch size={14} className="mr-1.5" /> Nouveau devis
            </Button>
            <Button variant="outline" onClick={() => openPicker()} className="text-xs font-semibold uppercase tracking-wider" data-testid="new-supplier-order-btn">
              <ShoppingCart size={14} className="mr-1.5" /> Nouvelle commande
            </Button>
            <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="add-supplier-btn">
              <Plus size={16} className="mr-2" /> Nouveau fournisseur
            </Button>
          </div>
        </div>
        <div className="mb-4">
          <BulkActionBar count={selection.count} actions={supplierBulkActions} onAction={handleBulkAction} disabled={bulkLoading} />
        </div>
        <div className="responsive-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10 hidden md:table-cell"><Checkbox checked={selection.isAllSelected} onCheckedChange={selection.toggleAll} data-testid="supplier-select-all" /></TableHead>
                <TableHead className="uppercase text-xs text-slate-500">Société</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Contact</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell">Email</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden xl:table-cell">Téléphone</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 hidden lg:table-cell">Ville</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-center hidden lg:table-cell">Factures</TableHead>
                <TableHead className="uppercase text-xs text-slate-500 text-right">Total TTC</TableHead>
                <TableHead className="uppercase text-xs text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {suppliers.map(s => (
                <TableRow key={s.id} className={`table-row-hover cursor-pointer ${selection.selected.has(s.id) ? "bg-indigo-50/50" : ""}`} onClick={() => navigate(`/fournisseurs/${s.id}`)}>
                  <TableCell className="hidden md:table-cell" onClick={e => e.stopPropagation()}><Checkbox checked={selection.selected.has(s.id)} onCheckedChange={() => selection.toggle(s.id)} data-testid={`supplier-select-${s.id}`} /></TableCell>
                  <TableCell className="font-medium text-xs sm:text-sm">
                    {s.company}
                    <div className="lg:hidden text-[10px] text-slate-400 font-normal">{s.contact_name || s.email || ""}</div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{s.contact_name || "-"}</TableCell>
                  <TableCell className="text-slate-500 hidden xl:table-cell">{s.email || "-"}</TableCell>
                  <TableCell className="text-slate-500 hidden xl:table-cell">{s.phone || "-"}</TableCell>
                  <TableCell className="text-slate-500 hidden lg:table-cell">{s.city || "-"}</TableCell>
                  <TableCell className="text-center hidden lg:table-cell">
                    {s.invoice_count > 0 ? (
                      <Badge variant="outline" className="text-xs font-mono" data-testid={`supplier-invoice-count-${s.id}`}>{s.invoice_count}</Badge>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs sm:text-sm" data-testid={`supplier-invoice-total-${s.id}`}>
                    {s.invoice_total_ttc > 0 ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(s.invoice_total_ttc) : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="hidden md:flex gap-1" onClick={e => e.stopPropagation()}>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/fournisseurs/${s.id}`)} className="h-7 px-2 text-xs" data-testid={`view-supplier-${s.id}`}><Eye size={12} className="mr-1" />Voir</Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}><Pencil size={13} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => remove(s.id)} data-testid={`delete-supplier-${s.id}`}><Trash2 size={13} /></Button>
                    </div>
                    <div className="md:hidden" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={16} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[140px]">
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => navigate(`/fournisseurs/${s.id}`)}><Eye size={12} className="mr-2" />Voir</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer" onClick={() => openEdit(s)}><Pencil size={12} className="mr-2" />Modifier</DropdownMenuItem>
                          <DropdownMenuItem className="text-xs cursor-pointer text-red-600" onClick={() => remove(s.id)}><Trash2 size={12} className="mr-2" />Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {suppliers.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Aucun fournisseur</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} fournisseur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <CompanyLookup onSelect={(r) => setForm(prev => ({ ...prev, company: r.nom || prev.company, siret: r.siret || prev.siret, address: r.adresse || prev.address, postal_code: r.code_postal || prev.postal_code, city: r.ville || prev.city }))} />
            <div><Label>Société *</Label><Input value={form.company} onChange={e => f("company", e.target.value)} data-testid="supplier-company-input" /></div>
            <div><Label>Contact</Label><Input value={form.contact_name} onChange={e => f("contact_name", e.target.value)} data-testid="supplier-contact-input" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => f("email", e.target.value)} data-testid="supplier-email-input" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={e => f("phone", e.target.value)} data-testid="supplier-phone-input" /></div>
            </div>
            <div><Label>Adresse</Label><Input value={form.address} onChange={e => f("address", e.target.value)} /></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Code postal</Label><Input value={form.postal_code} onChange={e => f("postal_code", e.target.value)} /></div>
              <div><Label>Ville</Label><Input value={form.city} onChange={e => f("city", e.target.value)} /></div>
              <div><Label>Pays</Label><Input value={form.country} onChange={e => f("country", e.target.value)} /></div>
            </div>
            <div><Label>SIRET</Label><Input value={form.siret} onChange={e => f("siret", e.target.value)} /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => f("notes", e.target.value)} rows={3} /></div>
            <Button onClick={save} className="w-full bg-slate-900 hover:bg-slate-800 text-white uppercase text-xs font-bold tracking-wider" data-testid="save-supplier-btn">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
      <SupplierPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} actionType="commande" onSupplierSelected={handleSupplierSelected} />
      <DevisUploadFlow open={devisFlowOpen} onOpenChange={setDevisFlowOpen} onComplete={handleDevisComplete} />
    </div>
  );
}
